/* eslint-disable @typescript-eslint/no-explicit-any */
import { getLog } from '@eigtech/ui-shared-logging'
import { Textarea } from '@eigtech/ui/components/textarea'
import { cn } from '@eigtech/ui/lib/utils'
import { $generateHtmlFromNodes } from '@lexical/html'
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin'
import {
  InitialConfigType,
  InitialEditorStateType,
  LexicalComposer,
} from '@lexical/react/LexicalComposer'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import DOMPurify from 'dompurify'
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  EditorState,
  LexicalEditor,
} from 'lexical'
import {
  BeautifulMentionComponentProps,
  BeautifulMentionsItem,
  BeautifulMentionsMenuItemProps,
  BeautifulMentionsMenuProps,
  BeautifulMentionsPlugin,
  createBeautifulMentionNode,
} from 'lexical-beautiful-mentions'
import {
  ElementType,
  ForwardedRef,
  ReactNode,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react'
import { Mention } from './types/Mention'
import { getMentionsFromEditorState } from './utils/getMentionsFromEditorState'

const log = getLog()

type BaseMentionsInputProps =
  | {
      items: Record<string, BeautifulMentionsItem[]>
      triggers?: never
      onSearch?: never
      searchDelay?: never
    }
  | {
      items?: never
      triggers: string[]
      onSearch: (trigger: string, queryString?: string | null) => Promise<BeautifulMentionsItem[]>
      searchDelay?: number
    }

export type MentionsInputProps = BaseMentionsInputProps & {
  autofocus?: boolean
  className?: string
  children?: ReactNode
  editorRef?: ForwardedRef<LexicalEditor>
  defaultValue?: string
  isDisabled?: boolean
  isReadOnly?: boolean
  menuItemComponent?: ElementType<BeautifulMentionsMenuItemProps>
  MentionComponent?: ElementType<BeautifulMentionComponentProps>
  onBlur?: React.FocusEventHandler<HTMLDivElement>
  onChange?: (value: string, editorState: EditorState) => any
  onMentionsChange?: (mentions: Mention[]) => any
}

export function MentionsInput({
  ref,
  autofocus,
  className,
  children,
  editorRef: propsEditorRef,
  defaultValue,
  isDisabled,
  isReadOnly,
  items,
  menuItemComponent,
  MentionComponent,
  searchDelay,
  triggers,
  onBlur,
  onChange,
  onMentionsChange,
  onSearch,
}: MentionsInputProps & { ref: React.Ref<HTMLDivElement> }) {
  const editorRef = useRef<LexicalEditor>(null!)
  useImperativeHandle(propsEditorRef, () => editorRef.current)

  const mentionsProps = { items, triggers, onSearch, searchDelay } as BaseMentionsInputProps

  const handleChange = useCallback(() => {
    const editorState = editorRef.current.getEditorState()

    if (onChange) {
      editorState.read(() => {
        // convert editor nodes to HTML and strip all HTML except for br tags
        // in the future we could potentially leave more tags in if
        // we want to support more HTML, or support editing notes
        const htmlString = $generateHtmlFromNodes(editorRef.current)
        const value = mentionsPurify.sanitize(htmlString, { ALLOWED_TAGS: ['br'] })

        onChange(value, editorState)
      })
    }

    if (onMentionsChange) {
      const mentions = getMentionsFromEditorState(editorState)
      onMentionsChange(mentions)
    }
  }, [onChange, onMentionsChange])

  const initialState = useCallback(() => {
    if (!defaultValue) return
    const root = $getRoot()
    root.clear()

    const paragraphNode = $createParagraphNode()
    const textNode = $createTextNode(defaultValue)

    paragraphNode.append(textNode)
    root.append(paragraphNode)
  }, [defaultValue])

  return (
    <div
      ref={ref}
      className={cn('relative flex flex-col', className)}
      data-slot="mentions-input"
      onBlur={(e: React.FocusEvent<HTMLDivElement, Element>) => {
        handleChange()
        onBlur?.(e)
      }}
    >
      <LexicalComposer initialConfig={makeEditorConfig({ MentionComponent, initialState })}>
        {children}
        <div className="relative flex grow flex-col">
          <RichTextPlugin
            ErrorBoundary={LexicalErrorBoundary}
            contentEditable={
              <Textarea
                className="grow overflow-auto bg-white/30"
                disabled={isDisabled}
                readOnly={isReadOnly}
                render={<ContentEditable />}
              />
            }
            placeholder={
              <span className="text-muted-foreground pointer-events-none absolute left-3 top-3 opacity-60">
                Enter some text...
              </span>
            }
          />
          <BeautifulMentionsPlugin
            creatable={false}
            emptyComponent={Empty}
            insertOnBlur={false}
            menuComponent={MentionsMenu}
            menuItemComponent={menuItemComponent ?? MentionsMenuItem}
            menuItemLimit={100}
            {...mentionsProps}
          />

          {autofocus && <AutoFocusPlugin defaultSelection="rootStart" />}
          {/* Currently we only support calling the onChange prop on blur,
            but we could do it on change to if needed. */}
          {/* <OnChangePlugin onChange={handleChange} /> */}
          <HistoryPlugin />
          <EditorRefPlugin editorRef={editorRef} />
        </div>
      </LexicalComposer>
    </div>
  )
}

export function MentionsMenu({ loading, ...other }: BeautifulMentionsMenuProps) {
  const className =
    'ring-foreground/10 bg-popover text-popover-foreground max-h-96 w-[fit-content] z-1400 min-w-[35ch] overflow-y-auto overflow-x-hidden rounded-md p-1 shadow-md m-0 outline-none ring-1 duration-100 absolute top-[2px] overflow-hidden whitespace-nowrap border'
  if (loading) {
    return <div className={className}>Loading...</div>
  }
  return (
    <ul
      className={className}
      style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}
      {...other}
    />
  )
}

export function MentionsMenuItem({
  selected,
  item: __item,
  itemValue: __itemValue,
  label: __label,
  ref,
  ...props
}: BeautifulMentionsMenuItemProps & { ref?: React.Ref<HTMLLIElement> }) {
  return (
    <BaseListItem
      ref={ref}
      className={cn(selected && 'bg-accent text-accent-foreground')}
      {...props}
    />
  )
}

function BaseListItem({ className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li
      className={cn(
        'outline-hidden relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm',
        className
      )}
      {...props}
    />
  )
}

function Empty() {
  return (
    <MentionsMenu>
      <BaseListItem>No results found</BaseListItem>
    </MentionsMenu>
  )
}

export function InlineMention({
  children,
  className,
  ...props
}: Pick<BeautifulMentionComponentProps, 'children'> & {
  className?: string
}) {
  return (
    <span className={cn('rounded-sm bg-blue-200 p-0.5 font-medium', className)} {...props}>
      {children}
    </span>
  )
}

function DefaultMentionComponent({
  trigger: __trigger,
  value: __value,
  data: __data,
  children,
  ...props
}: BeautifulMentionComponentProps) {
  return <InlineMention {...props}>{children}</InlineMention>
}

function makeEditorConfig({
  MentionComponent = DefaultMentionComponent,
  initialState,
}: {
  MentionComponent?: ElementType<BeautifulMentionComponentProps>
  initialState?: InitialEditorStateType
} = {}) {
  const editorConfig = {
    editorState: initialState,
    namespace: 'mentionsInput',
    onError(error: any) {
      log.error('MentionsInput error', { error })
      throw error
    },
    nodes: [...createBeautifulMentionNode(MentionComponent)],
  } satisfies InitialConfigType

  return editorConfig
}

// Make a custom instance of DOMPurify so that we can add a hook just for this component
const mentionsPurify = DOMPurify()
mentionsPurify.addHook('uponSanitizeElement', (node) => {
  if (!(node instanceof HTMLElement)) {
    return
  }

  // We want to wrap all mentions with [], this makes it easier
  // to support spaces in the names of people who are tagged
  if (node.tagName?.toLocaleLowerCase() === 'span') {
    const spanNode = node as HTMLSpanElement
    if (spanNode.dataset.lexicalBeautifulMention === 'true') {
      spanNode.textContent = `[${spanNode.textContent}]`
      return
    }
  }

  if (node.tagName?.toLocaleLowerCase() !== 'p') {
    return
  }

  // if paragraph already contains a line break, ignore it
  if (node.children.length === 1) {
    if (node.children[0].tagName.toLocaleLowerCase() === 'br') {
      return
    }
  }

  // paragraphs will be removed, so we need to add a line break in their stead
  node.insertAdjacentElement('afterend', document.createElement('br'))
})
